import i18n from "i18n";
import store from "stores";

import { isNil, cloneDeep, size } from 'lodash';

import orgService from "services/Auth.Organization.service";
import licenseService from "services/Auth.License.service";

import Nuclias from "const/nuclias";
import licenseUtil from "utils/license.util";

const t = i18n.t;
const { licenseConfig, organizationType, deviceType } = Nuclias;

const getFilter = (info, pagination, searchParams) => {
  if (info.isOrgLevel) {
    return {
      organizationId: info.orgId
    };
  } else {
    const skip = (pagination.current - 1) * pagination.pageLimit;
    const params = {
      mspId: info.orgId,
      limit: pagination.pageLimit,
      skip: skip > 0 ? skip : 0
    };
    if (!isNil(searchParams?.searchFields)) {
      for (const [key, value] of Object.entries(searchParams.searchFields)) {
        if (!isNil(value.value) && value.value !== '')
          params[key] = value.value;
      }
    }
    return params;
  }
};

const convertData = async (info, list) => {
  return new Promise(async (Resolve, Reject) => {
    if (!size(list)) { Resolve([]); }
    const result = list.map((data, index) => {
      const item = licenseUtil.getLicenseRemainDate(data);
      if (item.licenseState === licenseConfig.statusTypes.inactive) {
        item.remainDayCount = null;
      }
      const licenseDeviceType = item.deviceType === licenseConfig.deviceType.ap ? deviceType.ap
        : (item.deviceType === licenseConfig.deviceType.switch ? deviceType.switch : deviceType.gateway);
      return {
        disabled: info.actions?.editInvalid || false,
        licenseType: item.licenseType,
        licenseTypeLabel: item.licenseType === licenseConfig.usedTypes.used ? 'c29fb273b8' : '8a73657240',
        state: item.state,
        status: item.licenseState,
        licenseStateLabel: item.licenseState === licenseConfig.statusTypes.active ? '4d3d769b81'
          : (item.licenseState === licenseConfig.statusTypes.inactive ? '3cab03c00d' : 'b1b0643f87'),
        licenseKey: item.key,

        termDays: item.termDays || 0,
        period: item.period,
        termUnitLabel: item.termUnitLabel,
        isFree: item.isFree,
        deviceType: item.deviceType,
        licenseDeviceType: licenseDeviceType,
        deviceTypeLabel: item.deviceTypeLabel,

        /** msp **/
        preOrganizationName: item.preOrganizationName || '',
        organizationName: item.organizationName || '',
        organizationId: item.organizationId,
        orgUid: item.orgUid || '',
        orgType: item.orgType || '',
        orgTypeLabel: item.orgType === organizationType.si ? 'MSP' : 'ORG',
        /** msp **/

        releasedAtFormat: item.releasedAtFormat || '',
        releasedAtUnix: item.releasedAtUnix || 0,
        deviceUid: item.deviceUid || '',
        deviceModelName: item.deviceModelName || '',
        macAddress: item.macAddress || '',

        startDateFormat: item.startDateFormat || '',
        startDateUnix: item.startDateUnix || '',
        endDateFormat: item.endDateFormat || '',
        endDateUnix: item.endDateUnix || '',

        timeRemaining: item.remainDayCount || null,
        remainDayUnitLabel: item.remainDayUnit || '',
      };
    });
    Resolve(result);
  });
};

const translateList = (list) => {
  return new Promise(async (Resolve, Reject) => {
    let newList = cloneDeep(list);
    for (const [index, item] of newList.entries()) {
      for (let [key, value] of Object.entries(item)) {
        if (key.toLocaleLowerCase().indexOf('label') !== -1 && key.toLocaleLowerCase().indexOf('full') === -1) {
          if (key === 'termUnitLabel') {
            let label = `${item.period} ${t(value)}`;
            label += item.isFree ? ` (${t('6a4d6f2ef0')})` : '';
            newList[index][`${key}Full`] = label;
          } else if (key === 'remainDayUnitLabel') {
            if (item.status === licenseConfig.statusTypes.inactive) {
              newList[index][`${key}Full`] = ``;
            } else {
              newList[index][`${key}Full`] = `${item.timeRemaining} ${t(value)}`;
            }
          } else {
            newList[index][`${key}Full`] = t(value);
          }
        }
      }
    }
    Resolve(newList);
  });
};

const getOverviewData = async (orgId) => {
  return new Promise(async (Resolve, Reject) => {
    try {
      const data = await orgService.getLicenseOverview(orgId);
      Resolve(data.data);
    } catch (error) {
      Reject(error);
    }
  });
};

const getLicenseData = async (info, params) => {
  return new Promise(async (Resolve, Reject) => {
    try {
      const result = {};
      if (params.mspId) {
        const mspData = await mspApi(params);
        const data = await convertData(info, mspData.data);
        result.data = await translateList(data);
        result.total = mspData.total;
      } else {
        const data = await convertData(info, await orgApi(params));
        result.data = await translateList(data);
      }
      Resolve(result);
    } catch (error) {
      Reject(error);
    }
  });
};

const mspApi = async (params) => {
  return new Promise(async (Resolve, Reject) => {
    try {
      const data = await licenseService.getMspOverview(params);
      Resolve(data);
    } catch (error) {
      Reject(error);
    }
  });
};

const orgApi = async (params) => {
  return new Promise(async (Resolve, Reject) => {
    try {
      const data = await licenseService.getLicense(params);
      Resolve(data.data);
    } catch (error) {
      Reject(error);
    }
  });
};

const searchOrgOption = async (param) => {
  let skip = 0;
  if (param.data.length > 0 && param.search.total > param.data.length) {
    skip = Math.trunc(param.data.length / 10) * 10;
  }
  const ds = store.getState().pageContext.si;
  const params = {
    name: size(param.search.name) > 0 ? param.search.name : '%',
    skip,
    limit: 10
  };
  const res = await orgService.searchIndexOrg(ds.id, params);

  param.search.total = res.total;
  const resultData = skip > 0 ? param.data.concat(res.data) : res.data;

  let result = {
    search: param.search,
    data: resultData,
  };

  return result;
};

export default {
  getFilter,
  getOverviewData,
  getLicenseData,
  translateList,
  searchOrgOption,
};